import { createContext, useContext } from "react";

/**
 * Who currently owns the shared "Quoc Huy" / role text, shared between Intro
 * and Home via a matching Framer Motion `layoutId`.
 *
 * `layoutId` performs its shared-element animation by comparing, across one
 * React commit, which elements bearing a given id were removed and which
 * were added — so exactly one of Intro or Home may render that element at
 * any moment, and the handoff between them has to happen in a single state
 * flip for Framer to treat it as one continuous element moving, rather than
 * one thing disappearing and an unrelated thing appearing elsewhere.
 *
 * - "wait": an intro is in progress and hasn't handed off yet — Home renders
 *   nothing where its name/role would go, because Intro currently owns that
 *   layoutId.
 * - "morph": the handoff has happened — Home now renders the shared element,
 *   and Framer animates it from Intro's on-screen position/size to Home's.
 * - "normal": there was no intro this load (repeat visit this session, or
 *   `prefers-reduced-motion`) — Home renders its own independent reveal
 *   exactly as it did before any of this existed.
 */
export type HeroTextMode = "wait" | "morph" | "normal";

export interface IntroMorphState {
    heroTextMode: HeroTextMode;
    /**
     * True from mount until Intro has fully finished (background faded,
     * component unmounted) — spans "wait" *and* "morph", unlike
     * `heroTextMode`, which has no way to tell "just started morphing" apart
     * from "morphed a long time ago" once it settles on "morph" (it stays
     * that value for the rest of the page's life; see Home.tsx).
     *
     * Exists so heavy, purely decorative work — right now, the Lanyard 3D
     * scene — can wait until the shared-layout flight has completely
     * finished landing before it starts its own render loop, rather than
     * quietly spending frame budget on something invisible (hidden behind
     * Intro's opaque background) at exactly the moment that flight needs
     * every frame it can get. A frame-timing trace caught a ~550ms dropped
     * frame landing at the instant the morph began, with the 3D scene's
     * render loop already running underneath the whole time as the prime
     * suspect — this flag exists to test and fix that.
     */
    introActive: boolean;
}

const defaultState: IntroMorphState = { heroTextMode: "normal", introActive: false };

export const IntroMorphContext = createContext<IntroMorphState>(defaultState);

export function useIntroMorph(): IntroMorphState {
    return useContext(IntroMorphContext);
}
